import React, { useState } from 'react';
import { getModuleFeatures, setModuleFeature, resetModuleSettings, MODULE_FEATURE_DEFAULTS } from '../engine.js';

/* v8 Path P — ModuleSettingsPanel
   A compact, collapsible "⚙ Settings" panel that lives at the top of each
   module's overview. Toggles features on/off for that module only. Reads
   + writes `scenario.moduleSettings.{moduleId}.features`; every consumer
   (render guard in the tab) checks via `getModuleFeatures(scenario, id)`.

   Session-only by default (per E-016). Save a Model Profile to make
   a particular feature-gate configuration durable. Counterpart to the
   global verbosity picker in ScenarioBar — verbosity = "how much detail",
   per-module settings = "which features".

   Why in-tab (not global Settings): the mental model is "I'm looking at
   CMA, I want to toggle CMA-specific stuff right here". Purpose-built
   per-module depth, as the pattern doc demands.
*/
export default function ModuleSettingsPanel({ scenario, setScenario, moduleId, title, features }) {
  const [open, setOpen] = useState(false);
  const current = getModuleFeatures(scenario, moduleId);
  const defaults = MODULE_FEATURE_DEFAULTS[moduleId] || {};
  const modifiedCount = Object.keys(defaults).filter(k => current[k] !== defaults[k]).length;

  const toggle = (key) => {
    setScenario(setModuleFeature(scenario, moduleId, key, !current[key]));
  };
  const reset = () => {
    setScenario(resetModuleSettings(scenario, moduleId));
  };

  return (
    <div className="bg-surface-el border border-border rounded-md mb-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-[0.9rem] py-[0.55rem] bg-transparent border-0 cursor-pointer font-[inherit] text-[0.85rem] text-text hover:bg-surface-hover"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <span className="text-accent-gold text-[1rem] leading-none" aria-hidden>⚙</span>
          <strong>{title || `${moduleId.toUpperCase()} · module settings`}</strong>
          {modifiedCount > 0 && (
            <span
              className="text-[0.68rem] px-[6px] py-[1px] rounded-[3px] bg-[rgba(240,180,41,0.14)] text-accent-gold border border-accent-gold/50 uppercase tracking-[0.06em] font-bold"
              title="Non-default toggle(s) active"
            >
              ● {modifiedCount} modified
            </span>
          )}
        </span>
        <span className="text-text-dim text-[0.72rem]">{open ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      {open && (
        <div className="border-t border-border px-[0.9rem] py-[0.7rem]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-[0.5rem]">
            {features.map((f) => (
              <label
                key={f.key}
                className="flex items-start gap-2 px-[0.4rem] py-[0.3rem] rounded-sm hover:bg-surface cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!current[f.key]}
                  onChange={() => toggle(f.key)}
                  className="mt-[3px] accent-accent-gold cursor-pointer"
                />
                <span className="flex flex-col">
                  <span className="text-[0.82rem] text-text">{f.label}</span>
                  {f.hint && <span className="text-[0.7rem] text-text-dim">{f.hint}</span>}
                </span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-[0.7rem] pt-[0.6rem] border-t border-dashed border-border">
            <button
              type="button"
              onClick={reset}
              className="bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.3rem] rounded-sm cursor-pointer font-[inherit] text-[0.72rem] hover:text-text hover:border-accent-gold"
              disabled={modifiedCount === 0}
            >
              Reset {moduleId.toUpperCase()} to defaults
            </button>
            <span className="text-[0.7rem] text-text-dim italic">
              Feature flags are session-scoped. Save a Model Profile to keep them across reloads.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
